import { useState, useEffect } from 'react';
import { resolveAvatarUrl } from '../utils/imageUrl';
import { getInitials } from '../utils/initials';

/**
 * 아바타 배경용 고정 5색 팔레트 (src/index.css @theme 토큰 기반, 흰색 텍스트 대비 확보)
 * 색상 변경 시 아래 배열의 값만 교체하면 전역 컴포넌트에 즉시 반영됩니다.
 * 0: --color-primary (#006565)
 * 1: --color-moowoo-balance-dark (#0F5C57)
 * 2: --color-tertiary (#00691b)
 * 3: --color-moowoo-balance-error (#C0392B)
 * 4: --color-inverse-surface (#30312c)
 */
export const AVATAR_PALETTE = [
  '#006565',
  '#0F5C57',
  '#00691b',
  '#C0392B',
  '#30312c',
];

function getPaletteColor(userId) {
  if (!userId || typeof userId !== 'string') {
    return AVATAR_PALETTE[0];
  }
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = (hash << 5) - hash + userId.charCodeAt(i);
    hash |= 0;
  }
  const index = Math.abs(hash) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[index];
}

function getFontSizeClass(size) {
  if (!size) return 'text-[14px] font-semibold';
  if (size.includes('w-32') || size.includes('128')) return 'text-[46px] font-bold tracking-tight';
  if (size.includes('w-24') || size.includes('96')) return 'text-[34px] font-bold tracking-tight';
  if (size.includes('40') || size.includes('w-10')) return 'text-[15px] font-semibold tracking-tight';
  if (size.includes('w-9') || size.includes('36')) return 'text-[13px] font-semibold tracking-tight';
  if (size.includes('w-7') || size.includes('28')) return 'text-[11px] font-semibold';
  if (size.includes('w-6') || size.includes('24')) return 'text-[9.5px] font-semibold';
  if (size.includes('w-5') || size.includes('20')) return 'text-[9px] font-semibold';
  return 'text-[14px] font-semibold';
}

export default function Avatar({
  userId,
  name,
  avatarUrl,
  avatarSelected = false,
  size = 'w-9 h-9',
  className = '',
  onClick,
}) {
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [avatarUrl]);

  const hasAvatar = Boolean(
    avatarSelected && avatarUrl && typeof avatarUrl === 'string' && avatarUrl.trim()
  );

  if (hasAvatar && !imageError) {
    const src = resolveAvatarUrl(avatarUrl);
    return (
      <img
        src={src}
        alt={name || 'avatar'}
        onError={() => setImageError(true)}
        onClick={onClick}
        className={`rounded-full object-cover aspect-square shrink-0 ${size} ${className}`}
      />
    );
  }

  const hasName = Boolean(name && typeof name === 'string' && name.trim());
  const hasUserId = Boolean(userId && typeof userId === 'string' && userId.trim());

  // name 이 비어 있거나 userId 가 없으면 이니셜 글자와 해시 색상을 계산하지 않고 중립 회색 빈 원 렌더
  if (!hasName || !hasUserId) {
    return (
      <div
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onClick={onClick}
        className={`rounded-full shrink-0 aspect-square select-none overflow-hidden bg-surface-container-high ${size} ${className}`}
      />
    );
  }

  const initials = getInitials(name);
  const bgColor = getPaletteColor(userId);
  const fontSizeClass = getFontSizeClass(size);

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      style={{ backgroundColor: bgColor }}
      className={`rounded-full flex items-center justify-center text-white shrink-0 aspect-square select-none overflow-hidden ${fontSizeClass} ${size} ${className}`}
    >
      <span className="leading-none">{initials}</span>
    </div>
  );
}
